import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  ClickAwayListener,
  Divider,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import Fuse from "fuse.js";
import useDebounce from "../../../hooks/useDebounce";

const WellSearch = ({ wells, onSelect }) => {
  const searchRef = useRef(null);
  const [value, setValue] = useState("");
  const debounced = useDebounce(value, 200);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  const fuzzy = useMemo(() => {
    if (!wells?.length) return null;
    return new Fuse(wells, {
      ignoreLocation: true,
      keys: ["well_name"],
      threshold: 0.3,
    });
  }, [wells]);

  useEffect(() => {
    if (!fuzzy || !debounced) {
      setResults([]);
      return;
    }
    setResults(fuzzy.search(debounced).slice(0, 30));
  }, [debounced, fuzzy]);

  useEffect(() => {
    setOpen(results.length > 0);
  }, [results]);

  const handleClose = (event) => {
    if (searchRef.current && searchRef.current.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <>
      <TextField
        inputRef={searchRef}
        autoComplete="off"
        placeholder="Type a well name..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => !!value && setOpen(true)}
        type="search"
        value={value}
        variant="outlined"
        size="small"
        style={{ width: "100%", maxWidth: 360 }}
      />
      <Popper
        open={open}
        anchorEl={searchRef.current}
        placement="bottom-start"
        style={{ zIndex: 5 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            style={{
              width: "360px",
              maxHeight: "320px",
              overflowY: "auto",
            }}
          >
            <List dense>
              {results.map((r, i) => (
                <React.Fragment key={r.item.well_ndx}>
                  <ListItem
                    button
                    onClick={() => {
                      onSelect(r.item);
                      setValue(r.item.well_name);
                      setOpen(false);
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        {r.item.well_name}
                      </Typography>
                      {r.item.aquifer_completion && (
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          {r.item.aquifer_completion}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                  {i < results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default WellSearch;
